import "./i18n";
import React from "react";
import ReactDOM from "react-dom/client";
import { Capacitor, registerPlugin } from "@capacitor/core";
import App from "./App.jsx";
import "./styles.css";
import i18next from "i18next";

// REISOR_NATIVE_THEME_CHROME_V1_START
const ReisorThemeChrome =
  registerPlugin(
    "ThemeChrome"
  );

function syncNativeThemeChrome(
  value
) {
  const theme =
    value === "dark"
      ? "dark"
      : "light";

  try {
    if (
      !Capacitor.isNativePlatform()
    ) {
      return;
    }

    Promise.resolve(
      ReisorThemeChrome.apply({
        theme,
      })
    ).catch(
      () => {}
    );
  } catch {}
}
// REISOR_NATIVE_THEME_CHROME_V1_END

// REISOR_CANONICAL_THEME_BOOTSTRAP_V1_START
const REISOR_THEME_STORAGE_KEY = "uiTheme";
const REISOR_THEME_LEGACY_STORAGE_KEY = "reisor_pro_theme";

function readReisorBootstrapTheme() {
  try {
    const canonical = localStorage.getItem(
      REISOR_THEME_STORAGE_KEY
    );

    if (
      canonical === "light" ||
      canonical === "dark"
    ) {
      return canonical;
    }

    const legacy = localStorage.getItem(
      REISOR_THEME_LEGACY_STORAGE_KEY
    );

    if (
      legacy === "light" ||
      legacy === "dark"
    ) {
      localStorage.setItem(
        REISOR_THEME_STORAGE_KEY,
        legacy
      );

      localStorage.removeItem(
        REISOR_THEME_LEGACY_STORAGE_KEY
      );

      return legacy;
    }
  } catch {}

  return "light";
}

function applyReisorBootstrapTheme(value) {
  const theme =
    value === "dark"
      ? "dark"
      : "light";

  try {
    const root =
      document.documentElement;

    root.setAttribute(
      "data-theme",
      theme
    );

    root.setAttribute(
      "data-reisor-pro-theme",
      theme
    );

    root.classList.toggle(
      "dark",
      theme === "dark"
    );

    root.style.colorScheme =
      theme;

    let meta =
      document.querySelector(
        'meta[name="theme-color"]'
      );

    if (!meta) {
      meta =
        document.createElement(
          "meta"
        );

      meta.setAttribute(
        "name",
        "theme-color"
      );

      document.head.appendChild(
        meta
      );
    }

    meta.setAttribute(
      "content",
      theme === "dark"
        ? "#020617"
        : "#f3f4f6"
    );

    syncNativeThemeChrome(
      theme
    );
  } catch {}

  return theme;
}

applyReisorBootstrapTheme(
  readReisorBootstrapTheme()
);

window.addEventListener(
  "reisor-theme-change",
  (event) => {
    const value =
      event?.detail;

    if (
      value === "light" ||
      value === "dark"
    ) {
      applyReisorBootstrapTheme(
        value
      );
    }
  }
);

window.addEventListener(
  "storage",
  (event) => {
    if (
      event?.key ===
        REISOR_THEME_STORAGE_KEY &&
      (
        event?.newValue === "light" ||
        event?.newValue === "dark"
      )
    ) {
      applyReisorBootstrapTheme(
        event.newValue
      );
    }
  }
);
// REISOR_CANONICAL_THEME_BOOTSTRAP_V1_END

// REISOR_NATIVE_THEME_CHROME_RESUME_V1
function resyncNativeThemeChrome() {
  try {
    const current =
      document.documentElement
        .getAttribute(
          "data-theme"
        );

    syncNativeThemeChrome(
      current
    );
  } catch {}
}

window.addEventListener(
  "focus",
  resyncNativeThemeChrome
);

document.addEventListener(
  "visibilitychange",
  () => {
    if (
      !document.hidden
    ) {
      resyncNativeThemeChrome();
    }
  }
);


// REISOR_TG_NATIVE_ONLY_GATEWAY_V1
// Telegram in-app browsers may consume verified HTTPS App Links as normal web pages.
// For Telegram freight links we therefore use the HTTPS page only as a tiny bridge:
// explicitly launch the installed Android Capacitor package and never render Driver HUB.
const REISOR_DRIVER_PACKAGE = "online.cargo_platform.driver";
const REISOR_DRIVER_HOST = "driver.cargo-platform.online";

function telegramNativeGatewayRequest() {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get("tgNative") !== "1") return null;

    const freightId = String(
      url.searchParams.get("marketFreightId") || url.searchParams.get("freightId") || ""
    ).trim();
    if (!/^[A-Za-z0-9_-]{3,160}$/.test(freightId)) return null;

    const nativeQuery = new URLSearchParams();
    nativeQuery.set("marketFreightId", freightId);
    nativeQuery.set("register", "1");

    const playUrl = `https://play.google.com/store/apps/details?id=${encodeURIComponent(REISOR_DRIVER_PACKAGE)}`;
    const intentUrl =
      `intent://${REISOR_DRIVER_HOST}/?${nativeQuery.toString()}` +
      `#Intent;scheme=https;package=${REISOR_DRIVER_PACKAGE};` +
      `S.browser_fallback_url=${encodeURIComponent(playUrl)};end`;

    return { freightId, intentUrl, playUrl };
  } catch {
    return null;
  }
}

function launchTelegramNativeGateway(req) {
  if (!req) return;

  let backgrounded = false;
  const stopFallback = () => { backgrounded = true; };
  window.addEventListener("pagehide", stopFallback, { once: true });
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) backgrounded = true;
  }, { once: true });

  try {
    window.location.href = req.intentUrl;
  } catch {}

  // Some embedded browsers ignore intent:// navigation instead of honoring
  // browser_fallback_url. Never fall through to Driver web market: use Play Store.
  window.setTimeout(() => {
    if (!backgrounded) {
      try { window.location.replace(req.playUrl); } catch {}
    }
  }, 1600);
}

const telegramNativeGateway = telegramNativeGatewayRequest();

if (telegramNativeGateway) {
  document.documentElement.setAttribute("data-reisor-tg-native-gateway", "v1");
  launchTelegramNativeGateway(telegramNativeGateway);
}

// SW register ASAP (driver)
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch(() => {});
}

function isTouchLike() {
  try {
    return !!(
      window.matchMedia?.("(pointer:coarse)").matches ||
      /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "")
    );
  } catch {
    return false;
  }
}

function OfflineFallback({ checking, onRetry }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        background:
          "radial-gradient(circle at top, rgba(37,99,235,0.22), transparent 32%), #020617",
        color: "#e5eefc",
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: isTouchLike() ? "100%" : 520,
          background: "rgba(15,23,42,0.92)",
          border: "1px solid rgba(148,163,184,0.24)",
          borderRadius: 28,
          padding: 24,
          boxShadow: "0 20px 50px rgba(0,0,0,0.35)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
          <img
            src="/icons/icon-192.png"
            alt="REISOR PRO"
            style={{ width: 56, height: 56, borderRadius: 16 }}
          />
          <div>
            <div style={{ fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", color: "#94a3b8" }}>
              {i18next.t("appShell.driverPlatform")}
            </div>
            <div style={{ fontSize: 24, fontWeight: 800 }}>REISOR PRO</div>
          </div>
        </div>

        <h1 style={{ fontSize: 28, lineHeight: 1.1, margin: "0 0 12px" }}>
          {i18next.t("appShell.offlineTitle")}
        </h1>

        <p style={{ margin: "0 0 12px", color: "#94a3b8", lineHeight: 1.55 }}>
          {i18next.t("appShell.offlineDesc")}
        </p>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 18 }}>
          <button
            type="button"
            onClick={onRetry}
            disabled={checking}
            style={{
              appearance: "none",
              border: 0,
              borderRadius: 18,
              padding: "14px 18px",
              fontSize: 16,
              fontWeight: 800,
              cursor: checking ? "default" : "pointer",
              background: "linear-gradient(135deg, #10b981, #22c55e)",
              color: "#04121f",
              opacity: checking ? 0.8 : 1,
            }}
          >
            {checking ? i18next.t("appShell.checking") : i18next.t("appShell.retry")}
          </button>
        </div>
      </div>
    </div>
  );
}

function isNativeLikeRuntime() {
  try {
    const protocol = String(window.location.protocol || "").toLowerCase();
    const host = String(window.location.hostname || "").toLowerCase();
    return (
      protocol === "capacitor:" ||
      protocol === "file:" ||
      host === "localhost" ||
      host === "127.0.0.1"
    );
  } catch {
    return false;
  }
}

function backendProbeUrl() {
  try {
    const base = isNativeLikeRuntime()
      ? "https://backend.cargo-platform.online"
      : "";
    return `${base}/api/drivers/session`;
  } catch {
    return "/api/drivers/session";
  }
}

async function probeBackendOnline() {
  const timeoutMs = isNativeLikeRuntime() ? 6500 : 4500;

  try {
    const controller = new AbortController();
    const timeout = new Promise((_, reject) => {
      window.setTimeout(() => {
        try {
          controller.abort();
        } catch {}
        reject(new Error("NETWORK_PROBE_TIMEOUT"));
      }, timeoutMs);
    });

    const url = `${backendProbeUrl()}?probe=1&ts=${Date.now()}`;
    const request = fetch(url, {
      method: "GET",
      cache: "no-store",
      credentials: "include",
      signal: controller.signal,
    });

    // Любой HTTP-ответ = интернет и backend доступны.
    const res = await Promise.race([request, timeout]);
    return !!res;
  } catch {
    return false;
  }
}

async function probeInternetOnline() {
  const timeoutMs = isNativeLikeRuntime() ? 4500 : 3000;

  try {
    const controller = new AbortController();
    const timeout = new Promise((_, reject) => {
      window.setTimeout(() => {
        try {
          controller.abort();
        } catch {}
        reject(new Error("INTERNET_PROBE_TIMEOUT"));
      }, timeoutMs);
    });

    // This probe is intentionally independent from backend health. If backend is
    // slow but the public REISOR web edge is reachable, the app must stay open.
    const request = fetch(
      `https://driver.cargo-platform.online/icons/icon-192.png?connectivity=${Date.now()}`,
      {
        method: "GET",
        cache: "no-store",
        mode: "no-cors",
        signal: controller.signal,
      }
    );

    const res = await Promise.race([request, timeout]);
    return !!res;
  } catch {
    return false;
  }
}

function OfflineGuard({ children }) {
  const [online, setOnline] = React.useState(true);
  const [checking, setChecking] = React.useState(false);
  const failCountRef = React.useRef(0);

  const markOnline = React.useCallback(() => {
    failCountRef.current = 0;
    setOnline(true);
  }, []);

  const markOffline = React.useCallback((immediate = false) => {
    failCountRef.current += immediate ? 2 : 1;
    const limit = isNativeLikeRuntime() ? 2 : 3;
    if (failCountRef.current >= limit) {
      setOnline(false);
    }
  }, []);

  const syncConnectivity = React.useCallback(
    async ({ immediate = false } = {}) => {
      const browserReportsOffline =
        typeof navigator !== "undefined" &&
        navigator.onLine === false;

      // Backend latency/outage is not the same thing as loss of internet.
      // Keep the application open whenever either backend responds or the
      // independent REISOR web edge is reachable.
      if (!browserReportsOffline) {
        const backendOk = await probeBackendOnline();
        if (backendOk) {
          markOnline();
          return true;
        }
      }

      const internetOk = await probeInternetOnline();
      if (internetOk) {
        markOnline();
        return true;
      }

      markOffline(immediate);
      return false;
    },
    [markOffline, markOnline]
  );

  React.useEffect(() => {
    let dead = false;
    let firstProbeTimer = null;

    const safeSync = (opts) => {
      syncConnectivity(opts).catch(() => {
        if (!dead) markOffline(false);
      });
    };

    const handleOnline = () => {
      if (!dead) safeSync({ immediate: true });
    };

    const handleOffline = () => {
      // REISOR_DRIVER_OFFLINE_GUARD_NO_FLASH_V2: avoid 2-3 sec false no-internet screen on transient Android offline events.
      markOffline(false);
    };

    const handleResume = () => {
      if (!dead) safeSync({ immediate: false });
    };

    // В APK не блокируем старт мгновенным probe: на некоторых Android WebView
    // первый fetch после запуска даёт ложный сбой. Но если интернета реально нет,
    // системный offline event или 2 подряд probe покажут нормальную модалку.
    const firstDelay = isNativeLikeRuntime() ? 3500 : 400;
    firstProbeTimer = window.setTimeout(() => safeSync({ immediate: false }), firstDelay);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("focus", handleResume);
    document.addEventListener("visibilitychange", handleResume);

    const timer = window.setInterval(() => safeSync({ immediate: false }), 45000);

    return () => {
      dead = true;
      window.clearTimeout(firstProbeTimer);
      window.clearInterval(timer);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("focus", handleResume);
      document.removeEventListener("visibilitychange", handleResume);
    };
  }, [markOffline, syncConnectivity]);

  const retry = React.useCallback(async () => {
    setChecking(true);
    try {
      const ok = await syncConnectivity({ immediate: true });
      setOnline(ok);
    } finally {
      setChecking(false);
    }
  }, [syncConnectivity]);

  if (!online) {
    return <OfflineFallback checking={checking} onRetry={retry} />;
  }

  return children;
}

if (telegramNativeGateway) {
  ReactDOM.createRoot(document.getElementById("root")).render(
    <div
      data-reisor-tg-native-gateway-screen="v1"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: "#ffffff",
        color: "#0f172a",
        fontFamily: "system-ui, -apple-system, sans-serif",
        textAlign: "center",
      }}
    >
      <div style={{ maxWidth: 420 }}>
        <div style={{ fontSize: 28, fontWeight: 900, marginBottom: 10 }}>REISOR PRO</div>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 18 }}>Открываем заказ в приложении…</div>
        <a
          href={telegramNativeGateway.intentUrl}
          style={{
            display: "inline-block",
            borderRadius: 18,
            padding: "14px 20px",
            background: "#facc15",
            color: "#0f172a",
            textDecoration: "none",
            fontWeight: 900,
          }}
        >
          Открыть REISOR PRO
        </a>
      </div>
    </div>
  );
} else {
  ReactDOM.createRoot(document.getElementById("root")).render(
    <React.StrictMode>
      <OfflineGuard>
        <App />
      </OfflineGuard>
    </React.StrictMode>
  );
}
